import { useLazyQuery } from '@apollo/client'
import { Fade, IconButton, TableSortLabel } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import { Pagination } from '@material-ui/lab'
import { matchSorter } from 'match-sorter'
import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { CREDITORS_QUERY } from '../../graphql/walletCreditors'
import { getTranslate } from '../../localization'
import { Wallet } from '../../types/wallet'
import { CreditorFilters, CreditorListItem } from '../../types/walletCreditor'
import { WalletSortBy } from '../../types/walletEnums'
import { CreditorTableSkeleton } from './creditorTable.skeleton'
import { CreditorTableRowDesktop } from './creditorTableRow'
import useStyles from './styles/creditorContent.styles'

interface CreditorContentProps {
  wallet: Wallet
  setHasSettelment: React.Dispatch<React.SetStateAction<boolean>>
  searchInputValue: string
  setsearchInputValue: React.Dispatch<React.SetStateAction<string>>
}

export const CreditorContent: React.FC<CreditorContentProps> = ({
  wallet,
  setHasSettelment,
  searchInputValue
  // setsearchInputValue
}) => {
  const classes = useStyles()
  const params = useParams<{ walletId: string }>()
  const [sort, setSort] = useState<WalletSortBy>('default')
  const [filters, setFilters] = useState<CreditorFilters>({
    sortBy: 'default',
    walletID: params.walletId
  })
  const [page, setPage] = useState<number>(1)
  const [count, setcount] = useState(0)
  const [filteredCreditors, setFilteredCreditors] = useState<
    CreditorListItem[]
  >([])
  const perPage = 3
  const numberOfPages = useMemo(() => Math.ceil(count / perPage), [count])

  const [
    getCreditors,
    { data: creditors, loading: loadingForCreditors, refetch }
  ] = useLazyQuery<{
    creditors: CreditorListItem[]
  }>(CREDITORS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      setcount(res.creditors.length)
      const hasAnyDisabled = res.creditors.some((c) => !c.autoSettelmentEnabled)
      setHasSettelment(hasAnyDisabled)
    }
  })

  interface HeadCell {
    label: string
    id: string
  }
  const headCells: HeadCell[] = [
    { id: '1', label: 'Entity' },
    { id: 'wallet', label: 'Wallet Balance' },
    { id: 'credit', label: 'Remaining Credit Balance' },
    { id: '2', label: 'Remaining Reservable' },
    { id: '3', label: 'Active' }
  ]

  useEffect(() => {
    getCreditors({
      variables: {
        filters: {
          walletID: params.walletId,
          sortBy: sort
        }
      }
    })
  }, [params, sort])

  useEffect(() => {
    setPage(1)
    const fullCreditorList = creditors?.creditors ? creditors?.creditors : []
    const newFilteredCreditors = searchInputValue
      ? matchSorter(fullCreditorList, searchInputValue, {
          keys: ['entity.entityName']
        })
      : fullCreditorList

    setFilteredCreditors(newFilteredCreditors)
    setcount(newFilteredCreditors.length)
  }, [searchInputValue, loadingForCreditors])

  return (
    <div>
      <div className={classes.tableHead}>
        {headCells.map((item, index) => (
          <div key={index}>
            <TableSortLabel
              className={classes.tableSortLabel}
              active={
                item.id === 'wallet' || item.id === 'credit' ? true : false
              }
              onClick={() => {
                setSort(
                  item.id === 'wallet' || item.id === 'credit'
                    ? item.id
                    : 'decwallet'
                )
                setFilters({
                  ...filters,
                  sortBy:
                    item.id === 'wallet' || item.id === 'credit'
                      ? item.id
                      : 'decwallet'
                })
                if (sort === item.id) {
                  setSort(('dec' + item.id) as WalletSortBy)
                  setFilters({
                    ...filters,
                    sortBy: ('dec' + item.id) as WalletSortBy
                  })
                }
              }}
              hideSortIcon
              direction={item.id === sort ? 'desc' : 'asc'}>
              {getTranslate(item.label)}
            </TableSortLabel>
          </div>
        ))}
        <div className={classes.refreshButtonContainer}>
          <IconButton
            onClick={() => {
              refetch()
            }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
      {loadingForCreditors
        ? Array(3)
            .fill('')
            .map((ـ, index) => <CreditorTableSkeleton key={index} />)
        : filteredCreditors
            .slice((page - 1) * perPage, page * perPage)
            .map((singleCreditor, index) => (
              <Fade in timeout={600} key={index}>
                <CreditorTableRowDesktop
                  data={singleCreditor}
                  wallet={wallet}
                  refetch={refetch}
                />
              </Fade>
            ))}
      <div className={classes.paginationContainer}>
        <Pagination
          count={numberOfPages}
          color="primary"
          page={page}
          onChange={(_, value) => {
            setPage(value)
          }}
        />
      </div>
    </div>
  )
}
